/**
 * The ONE way a module emits its CSS as a <style> element.
 *
 * Module CSS strings carry long source comments (design notes, owner quotes).
 * They belong in the code, not in the page a visitor or crawler downloads, so
 * every module <style> goes through here and the comments are dropped on the
 * way out. Only bang comments (a slash-star followed by an exclamation mark, the
 * license convention) survive. String and
 * url() contents are never touched, so the rules themselves are byte-for-byte
 * what the source says.
 *
 * THE GUARANTEE IS ONLY AS WIDE AS THIS FUNCTION. It cleans the rendered
 * <style> HTML. A "use client" file is also bundled into the public client JS
 * chunk, and a CSS template literal in it ships there verbatim, comments and
 * all, because the minifier keeps string contents. So in a "use client" file the
 * design notes go in ordinary JS comments above the const, outside the
 * template, and only bang comments stay inside the string. A server file may
 * keep notes in its CSS string, since its string never reaches the client
 * bundle. check:client-css-comments enforces the client half.
 */

const cache = new Map<string, string>()

export function stripCssComments(css: string): string {
  const hit = cache.get(css)
  if (hit !== undefined) return hit
  let out = ""
  let i = 0
  const n = css.length
  while (i < n) {
    const c = css[i]
    if (c === '"' || c === "'") {
      // Copy a quoted string whole, honouring backslash escapes.
      let j = i + 1
      while (j < n && css[j] !== c) j += css[j] === "\\" ? 2 : 1
      out += css.slice(i, j + 1)
      i = j + 1
    } else if ((c === "u" || c === "U") && css.slice(i, i + 4).toLowerCase() === "url(" && !/[\w-]/.test(css[i - 1] ?? "")) {
      // An unquoted url() may hold "/*" (url(/img/*.png)); copy it whole up to
      // its closing parenthesis. A quoted one falls through to the string branch.
      let j = i + 4
      while (j < n && /\s/.test(css[j])) j++
      if (css[j] === '"' || css[j] === "'") {
        out += css.slice(i, j)
        i = j
      } else {
        while (j < n && css[j] !== ")") j += css[j] === "\\" ? 2 : 1
        out += css.slice(i, j + 1)
        i = j + 1
      }
    } else if (c === "/" && css[i + 1] === "*") {
      const end = css.indexOf("*/", i + 2)
      const stop = end === -1 ? n : end + 2
      if (css[i + 2] === "!") {
        out += css.slice(i, stop)
      } else {
        // A comment separates tokens, so keep one space where two word
        // characters would otherwise fuse (`a/**/b`).
        const prev = out[out.length - 1]
        const next = css[stop]
        if (prev && next && /[\w-]/.test(prev) && /[\w-]/.test(next)) out += " "
      }
      i = stop
    } else {
      out += c
      i++
    }
  }
  cache.set(css, out)
  return out
}

export function ModuleStyle({ css }: { css: string }) {
  return <style dangerouslySetInnerHTML={{ __html: stripCssComments(css) }} />
}
